import {objectFromEntries} from '@benchling/js-utils';

import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import ImmutableSet from 'util/ImmutableSet';

import {ParameterOptimizationFeature} from './ParameterOptimizationTypes';

/** @package */
export interface BaseEntitiesToBaseFeaturesMap {
  [entity: string]: ImmutableSet<string>;
}

/**
 * Given a featureImportance or shapValues data frame, return a map of its `baseEntity`s and their `baseFeature`
 * as `ImmutableSet`s.
 *
 * @package
 */
export function getBaseEntitiesToBaseFeaturesMap(
  dataFrame: DataFrame,
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>,
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>
): BaseEntitiesToBaseFeaturesMap {
  return dataFrame.rows.reduce<{[entity: string]: ImmutableSet<string>}>((entityToBaseFeatures, row) => {
    const baseEntities = baseEntitiesAccessor(row);
    const baseFeatures = baseFeaturesAccessor(row);

    return {
      ...entityToBaseFeatures,
      ...objectFromEntries(
        baseEntities.map((entity) => [
          entity,
          new ImmutableSet([...(entityToBaseFeatures[entity] ?? new Set()), ...baseFeatures]),
        ])
      ),
    };
  }, {});
}

/**
 * Given a `baseEntity`, `baseFeature`, and `baseEntitiesToBaseFeatureMap`, return the `ParameterOptimizationFeature`
 * for that feature.
 *
 * The `displayName` of the feature will be `baseFeature` unless it's not unique in `baseEntitiesToBaseFeatureMap`,
 * then it will be "`baseEntity`.`baseFeature`"
 */
export function makeParameterOptimizationFeature(
  baseEntity: string,
  baseFeature: string,
  baseEntitiesToBaseFeatureMap: BaseEntitiesToBaseFeaturesMap
): ParameterOptimizationFeature {
  const existsInOtherBaseEntity = Object.keys(baseEntitiesToBaseFeatureMap).some(
    (otherBaseEntity) =>
      baseEntity !== otherBaseEntity && baseEntitiesToBaseFeatureMap[otherBaseEntity].has(baseFeature)
  );

  return {
    baseEntity,
    baseFeature,
    displayName: existsInOtherBaseEntity ? `${baseEntity}.${baseFeature}` : baseFeature,
  };
}

/**
 * Given a featureImportance or shapValues data frame, return a list of its `ParameterOptimizationFeature`s in
 * order that they appear in the data frame.
 *
 * If there are 2 `baseFeature`s with the same name and different `baseEntity` values, their `displayName`s will be returned
 * as "`baseEntity`.`baseFeature`" instead of the usual "`baseFeature`".
 *
 * @package
 */
export function getFeatureList(
  dataFrame: DataFrame,
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>,
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>
): Array<ParameterOptimizationFeature> {
  const baseEntitiesToBaseFeatureMap = getBaseEntitiesToBaseFeaturesMap(
    dataFrame,
    baseEntitiesAccessor,
    baseFeaturesAccessor
  );

  const baseEntities = Object.keys(baseEntitiesToBaseFeatureMap);

  return baseEntities.flatMap((baseEntity) =>
    [...baseEntitiesToBaseFeatureMap[baseEntity]].map((baseFeature) =>
      makeParameterOptimizationFeature(baseEntity, baseFeature, baseEntitiesToBaseFeatureMap)
    )
  );
}
